import { Formik, Field } from 'formik'
import * as Yup from 'yup'
import classnames from 'classnames'
import { useContext, useState, useEffect } from 'react'
import { StoreContext } from '~/store'
import { updateUser, updateMarketingConsent, deleteUser, logoutDevice, sendEmailVerification, updatePhoneNumber, getUser } from '~/lib/api'
import { formatPhoneNumber } from '~/utils/format'
import { showModal } from '~/lib/modal'
import { SimpleSwitch } from '~/components'
import Header from '../Header'
import Menu from '../Menu'
import EmailVerificationModal from '~/components/EmailVerificationModal'
import PhoneVerificationModal from '~/components/PhoneVerificationModal'
import * as styles from './index.module.css'
import { useTranslation } from 'react-i18next'

const NotVerified = ({ t, onVerify, isLoading, label }) => (
  <span className={styles.notVerified}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    {t('label-not-verified')}
    {onVerify && (
      <button
        type="button"
        className={styles.verifyButton}
        onClick={onVerify}
        disabled={isLoading}
      >
        {isLoading ? '...' : (label || t('button-verify-email'))}
      </button>
    )}
  </span>
)

export default function ProfileView() {
  const { t } = useTranslation()
  const { user, setUser, logoutUser } = useContext(StoreContext)
  const [message, setMessage] = useState()
  const [loading, setLoading] = useState(false)
  const [verifyLoading, setVerifyLoading] = useState(false)
  const [verifyCompanyLoading, setVerifyCompanyLoading] = useState(false)
  const [showVerifyModal, setShowVerifyModal] = useState(false)
  const [verifyEmail, setVerifyEmail] = useState(null)
  const [verifyPhoneLoading, setVerifyPhoneLoading] = useState(false)
  const [showVerifyPhoneModal, setShowVerifyPhoneModal] = useState(false)
  const [verifyPhone, setVerifyPhone] = useState(null)

  useEffect(() => {
    if (!user?.userId) return
    const interval = setInterval(() => {
      getUser(user.userId).then(response => {
        const prevCompanyEmailVerified = user.companyConnections?.[0]?.emailVerified
        const nextCompanyEmailVerified = response.companyConnections?.[0]?.emailVerified
        if (response.emailVerified !== user.emailVerified || response.phoneVerified !== user.phoneVerified || nextCompanyEmailVerified !== prevCompanyEmailVerified) {
          setUser(response)
        }
      }).catch(() => {})
    }, 20000)
    return () => clearInterval(interval)
  }, [user?.userId, user?.emailVerified, user?.phoneVerified])

  if (!user) return null

  const company = user && user?.companyConnections?.length > 0 ? user?.companyConnections[0] : false

  const deleteProfile = async () => {
    console.log('deleteProfile:', user.userId)
    setLoading(true)
    deleteUser(user.userId).then((response) => {
      setMessage('')
      logoutUser()
    })
    .catch((error) => {
      console.error(error)
      setMessage(error?.response?.message)
    })
    .finally(() => {
      setLoading(false)
    })
  }

  const logout = async () => {
    setLoading(true)
    logoutDevice().then((response) => {
      setMessage('')
      logoutUser()
    })
    .catch((error) => {
      console.error(error)
      setMessage(error?.response?.message)
    })
    .finally(() => {
      setLoading(false)
    })
  }

  // The OTP is validated inside the modal, but the new number only lands on the
  // user object server side, so re-fetch to pick up phone/phoneVerified.
  const handlePhoneVerified = () => {
    setShowVerifyPhoneModal(false)
    setMessage('')
    getUser(user.userId)
      .then(response => setUser(response))
      .catch(error => setMessage(error?.response?.message))
  }

  const onDeleteClick = () => {
    showModal(COPY.my_pages.delete_profile_confirm, [
      { title: COPY.booking.button_ok, callback: deleteProfile },
      { title: COPY.booking.button_cancel },
    ])
  }

  const initialValues = {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email || user.username,
    phone: user.phone || '',
    marketingOptIn: user?.marketingOptIn?.consent
  }

  const COPY = window.COPY

  return (
    <>
      {showVerifyModal && (
        <EmailVerificationModal
          email={verifyEmail}
          userId={user.userId}
          onClose={() => setShowVerifyModal(false)}
        />
      )}
      {showVerifyPhoneModal && (
        <PhoneVerificationModal
          phone={verifyPhone}
          onVerified={handlePhoneVerified}
          onClose={() => setShowVerifyPhoneModal(false)}
        />
      )}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={Yup.object({
          firstName: Yup.string().required(),
          lastName: Yup.string().required(),
          email: Yup.string().email().required(),
          phone: Yup.string()
            .matches(/^\+?[\d\s\-()./]+$/, t('validation-phone-format'))
            .test('length', t('validation-phone-format'), value => {
              const digits = formatPhoneNumber(value).replace(/\D/g, '')
              return digits.length >= 8 && digits.length <= 16
            })
            .required(t('validation-need-phone')),
        })}
        onSubmit={async (values) => {
          setLoading(true)
          updateUser(user.userId, values).then((response) => {
            setMessage('')
            setUser(response)
          })
          .catch((error) => {
            console.log(error)
            setMessage(error?.response?.message)
          })
          .finally(() => {
            setLoading(false)
          })
        }}
      >
        {({ errors, submitForm, values: formValues }) => {
          const originalEmail = user.email || user.username
          const emailChanged = formValues.email !== originalEmail
          const showVerifyBadge = user.emailVerified === false || emailChanged

          const phoneChanged = formatPhoneNumber(formValues.phone) !== formatPhoneNumber(user.phone)
          const showPhoneVerifyBadge = user.phoneVerified === false || phoneChanged

          // Phone can't go through updateUser — it needs an OTP, so it goes via
          // the activation endpoint and is only persisted once the code is validated.
          const handleVerifyPhone = () => {
            if (errors?.phone) { setMessage(errors.phone); return }
            setVerifyPhoneLoading(true)
            const formatted = formatPhoneNumber(formValues.phone)
            updatePhoneNumber(formatted)
              .then(response => {
                if (response?.deviceActivationState === 'UPDATE_PHONE_OTP_INITIATED' ||
                    response?.expectedActions?.includes('VALIDATE_PHONE_OTP')) {
                  setMessage('')
                  setVerifyPhone(formatted)
                  setShowVerifyPhoneModal(true)
                } else {
                  console.warn('Unhandled deviceActivationState:', response?.deviceActivationState, response)
                  setMessage(t('error-phone-verify-failed'))
                }
              })
              .catch(error => setMessage(error?.response?.message))
              .finally(() => setVerifyPhoneLoading(false))
          }

          const handleVerifyEmail = () => {
            setVerifyLoading(true)
            const doVerify = (email) => {
              setVerifyEmail(email)
              sendEmailVerification(user.userId, email)
                .then(() => setShowVerifyModal(true))
                .catch(error => setMessage(error?.response?.message))
                .finally(() => setVerifyLoading(false))
            }
            if (emailChanged) {
              updateUser(user.userId, formValues)
                .then(response => { setUser(response); doVerify(formValues.email) })
                .catch(error => { setMessage(error?.response?.message); setVerifyLoading(false) })
            } else {
              doVerify(originalEmail)
            }
          }

          const handleVerifyCompanyEmail = () => {
            setVerifyCompanyLoading(true)
            setVerifyEmail(company.userCompanyEmail)
            sendEmailVerification(user.userId, company.userCompanyEmail)
              .then(() => setShowVerifyModal(true))
              .catch(error => setMessage(error?.response?.message))
              .finally(() => setVerifyCompanyLoading(false))
          }

          return (
          <section className="profile-view">
            <Header />
            <div className="my-pages-container">
              <Menu />
              <div className="my-pages-content">
                <h2>{COPY.my_pages.update_profile}</h2>
                <div className={classnames('my-pages-form profile-form', { loading })}>
                  <header className="form-header">{COPY.my_pages.details_header}</header>
                  <div className="form-group">
                    <label htmlFor="firstName">{t('label-firstname')}</label>
                    <Field name="firstName" className={classnames('input-field has-label', { 'has-error': errors?.firstName })}/>
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">{t('label-lastname')}</label>
                    <Field name="lastName" className={classnames('input-field has-label', { 'has-error': errors?.lastName })}/>
                  </div>
                  <div className={classnames('form-group', { [styles.withVerify]: showVerifyBadge })}>
                    <label htmlFor="email">{COPY.my_pages.label_email}</label>
                    <Field name="email" className={classnames('input-field has-label', { 'has-error': errors?.email })}/>
                    {showVerifyBadge && (
                      <NotVerified t={t} onVerify={handleVerifyEmail} isLoading={verifyLoading} />
                    )}
                  </div>
                  <div className={classnames('form-group', { [styles.withVerify]: showPhoneVerifyBadge })}>
                    <label htmlFor="phone">{t('label-mobile-phone')}</label>
                    <Field name="phone" type="tel" inputMode="tel" autoComplete="tel" className={classnames('input-field has-label', styles.phoneGroup, { 'has-error': errors?.phone })}/>
                    {showPhoneVerifyBadge && (
                      <NotVerified t={t} onVerify={handleVerifyPhone} isLoading={verifyPhoneLoading} label={t('button-verify-phone')} />
                    )}
                    <p className={styles.phoneInfo}>{t('info-verify-phone')}</p>
                  </div>
                  {company && <div className={classnames('form-group', styles.companyGroup)}>
                    <label>{t('label-company')}</label>
                    {company?.name}<br />
                    <p className={styles.emailInfo}>{t('label-connected-email')}</p>
                    <div className={classnames({ [styles.withVerify]: company.emailVerified === false })}>
                      {company?.userCompanyEmail}
                      {company.emailVerified === false && (
                        <NotVerified t={t} onVerify={handleVerifyCompanyEmail} isLoading={verifyCompanyLoading} />
                      )}
                    </div>
                  </div>}
                  <div className="form-group">
                    <label htmlFor="marketingOptIn">{t('label-communications')}</label>
                    <div className={styles.wrapper}>
                      <p className={styles.marketingInfo}>{t('title-accept-marketing')}</p>
                      <Field name="marketingOptIn">
                        {({ field, form }) => (
                          <SimpleSwitch
                            field={{
                              ...field,
                              onChange: (e) => {
                                const checked = e.target.checked
                                form.setFieldValue('marketingOptIn', checked, false)
                                updateMarketingConsent(user.userId, checked)
                                  .then(response => setUser(response))
                                  .catch(error => setMessage(error?.response?.message))
                              }
                            }}
                            form={form}
                          />
                        )}
                      </Field>
                    </div>
                  </div>
                  <div className="form-group with-padding remove-top-padding">
                    <div className={classnames('message', { show: message })}>{message}</div>
                    <button id="save-profile-button" className="btn" onClick={submitForm}>{COPY.my_pages.save_profile_button}</button>
                    <button id="delete-profile-button" className="btn gray" onClick={onDeleteClick}>{COPY.my_pages.delete_profile_button}</button>
                    <button id="logout-button" className="btn gray" onClick={logout}>{COPY.my_pages.logout_button}</button>
                  </div>
                </div>

              </div>
            </div>
          </section>
          )
        }}
      </Formik>
    </>
  )
}
